import { Injectable } from '@angular/core';
import {
  DwModalButtonStandardConfigs, DwModalConfig, DwModalService, DwModalSize, DwSecurityUserService
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, mergeMap } from 'rxjs/operators';
import { StudentOnboardingActionService } from '../../students/actions/student-onboarding-action.service';
import { PostLoginAcknowledgementModalComponent } from '../components/post-login-acknowledgement-modal/post-login-acknowledgement-modal.component';
import { PostLoginExperience } from '../models/post-login-experience.model';
import { PostLoginRepositoryService } from './post-login-repository.service';
import { IntegrationUtilService } from '../../integration/services/integration-util.service';

@Injectable({ providedIn: 'root' })
export class PostLoginExperienceService {
  private running = false;

  constructor(
    private repository: PostLoginRepositoryService,
    private modalService: DwModalService,
    private onboardingAction: StudentOnboardingActionService,
    private integrationUtilService: IntegrationUtilService,
    private securityUserService: DwSecurityUserService
  ) { }

  run(): void {
    if (this.running || this.isEmulatingUser()) {
      return;
    }
    this.running = true;
    this.loadExperienceAfterIntegration()
      .pipe(
        mergeMap(experience => this.showAcknowledgements(experience)
          .pipe(map(() => experience))),
        mergeMap(experience => this.showOnboarding(experience)),
        catchError(() => of(null)),
        finalize(() => this.running = false)
      )
      .subscribe();
  }

  private loadExperienceAfterIntegration(): Observable<PostLoginExperience> {
    const integrationVersion = this.integrationUtilService.getIntegrationWorkflowVersion();
    return this.integrationUtilService.waitForIntegrationWorkflow().pipe(
      mergeMap(() => this.repository.getExperience()),
      mergeMap(experience => this.integrationUtilService.waitForIntegrationWorkflow().pipe(
        mergeMap(() => integrationVersion === this.integrationUtilService.getIntegrationWorkflowVersion()
          ? of(experience)
          : this.loadExperienceAfterIntegration())
      ))
    );
  }

  showAcknowledgements(experience: PostLoginExperience, allowEmulatedUser: boolean = false): Observable<any> {
    if (this.isEmulatingUser() && !allowEmulatedUser) {
      return of(null);
    }

    const hasAcknowledgements = !!experience.Terms
      || !!experience.Documents?.length
      || !!experience.Assignments?.length;
    if (!hasAcknowledgements) {
      return of(null);
    }

    const config = new DwModalConfig<PostLoginExperience>();
    config.data = experience;
    config.component = PostLoginAcknowledgementModalComponent;
    config.title = 'Required acknowledgements';
    config.modalSize = DwModalSize.responsive;
    config.customModalWidth = '680px';
    config.preventClose = true;
    config.hideFooter = true;
    config.buttons = [DwModalButtonStandardConfigs.instance.okButton];
    return this.modalService.showModal(config);
  }

  private showOnboarding(experience: PostLoginExperience): Observable<any> {
    if (this.isEmulatingUser() || !experience.Onboarding?.ShowAutomatically || !experience.Onboarding.Role) {
      return of(null);
    }
    return this.onboardingAction.showOnboarding(experience.Onboarding.Role)
      .pipe(mergeMap(result => result.cancelled || this.isEmulatingUser()
        ? of(null)
        : this.repository.completeOnboarding(experience.Onboarding.Version)));
  }

  private isEmulatingUser(): boolean {
    const securityContext = this.securityUserService.securityState?.securityContext;
    const authenticatedUserId = securityContext?.AuthenticatedUser?.UserId;
    const applicationUserId = securityContext?.ApplicationUser?.UserId;

    return !!authenticatedUserId && !!applicationUserId && authenticatedUserId !== applicationUserId;
  }
}
